import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, ChevronDown, CheckCircle, Shield, Heart, Flame, Anchor, Eye, Users, CreditCard } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const SERVICES = [
  { icon: CheckCircle, label: "Erdbestattung", desc: "Traditionelle Beerdigung mit persönlicher Gestaltung der Trauerfeier." },
  { icon: Flame, label: "Feuerbestattung", desc: "Einäscherung mit anschließender Urnenbeisetzung oder -aufbewahrung." },
  { icon: Anchor, label: "Seebestattung", desc: "Besondere Beisetzung auf See in der Deutschen Bucht oder der Ostsee." },
  { icon: Eye, label: "Anonymbestattung", desc: "Würdevolle Bestattung ohne namentliche Kennzeichnung der Grabstätte." },
  { icon: Heart, label: "Trauerbegleitung", desc: "Einfühlsame Begleitung für Angehörige vor, während und nach der Bestattung." },
  { icon: Users, label: "Trauerfeier", desc: "Gestaltung einer persönlichen, würdevollen Trauerfeier nach Ihren Wünschen." },
  { icon: CreditCard, label: "Finanzierung", desc: "Flexible Zahlungsvereinbarungen, Vorsorgeverträge und Beratung zur Bestattungsvorsorge." },
  { icon: Shield, label: "Bestattungsvorsorge", desc: "Rechtzeitig vorsorgen und Angehörige entlasten – wir beraten Sie ausführlich." },
];

const FAQ = [
  {
    q: "Was muss ich im Trauerfall als Erstes tun?",
    a: "Rufen Sie uns an – rund um die Uhr, 365 Tage im Jahr: 0171 4312224. Wir übernehmen sofort alle notwendigen Schritte für Sie.",
  },
  {
    q: "Kümmern Sie sich auch um alle Formalitäten?",
    a: "Ja. Wir übernehmen Überführung, Einbettung, Sargträger sowie alle Behördengänge und die Kommunikation mit Friedhof und Pfarrei.",
  },
  {
    q: "Wie schnell können Sie nach einem Todesfall kommen?",
    a: "Wir sind 24 Stunden täglich erreichbar und kommen so schnell wie möglich zu Ihnen. Im Notfall innerhalb weniger Stunden.",
  },
  {
    q: "Was kostet eine Bestattung?",
    a: "Die Kosten variieren je nach Art der Bestattung und Ihren Wünschen. Wir beraten Sie transparent und ohne versteckte Kosten – gemeinsam finden wir eine Lösung, die zu Ihnen passt.",
  },
  {
    q: "Was ist Bestattungsvorsorge?",
    a: "Bei der Vorsorge legen Sie Ihre Wünsche für die eigene Bestattung schriftlich fest und können die Kosten bereits im Voraus regeln. Das entlastet Ihre Angehörigen erheblich.",
  },
  {
    q: "Können wir den Sarg selbst auswählen oder gestalten?",
    a: "Absolut. Da wir auch eine eigene Schreinerei betreiben, können wir individuelle Wünsche bei der Sarggestaltung besonders gut erfüllen.",
  },
];

const FAQItem = ({ q, a, index, open, setOpen }) => {
  const isOpen = open === index;
  return (
    <div className="border-b border-[#3E4C59]/20" data-testid={`faq-item-${index}`}>
      <button
        className="w-full py-5 flex items-center justify-between text-left gap-4"
        onClick={() => setOpen(isOpen ? null : index)}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-manrope text-[#2B2B2B] font-medium text-sm sm:text-base">{q}</span>
        <ChevronDown size={18} className={`shrink-0 text-[#3E4C59] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-[#2B2B2B]/60 text-sm font-manrope leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Bestattungen = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <main>
      {/* HERO */}
      <section className="relative h-[65vh] overflow-hidden" data-testid="bestattungen-hero">
        {/* PLATZHALTER: durch echtes Foto (Bestattungshaus Mühlenstraße 6 oder Natur/Ruhe-Motiv) ersetzen */}
        <div
          className="absolute inset-0 bg-cover bg-center animate-ken-burns"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1920&auto=format&fit=crop')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3E4C59]/75 to-[#2B2B2B]/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pb-16">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <p className="text-[#C9A66B] uppercase tracking-[0.35em] text-xs font-manrope mb-4">Bestattungen Loos</p>
              <h1 className="font-playfair text-3xl sm:text-5xl lg:text-6xl text-white leading-tight italic mb-3">
                "Trauern heißt nicht, lautes Klagen.
              </h1>
              <h2 className="font-playfair text-3xl sm:text-5xl lg:text-6xl text-[#C9A66B] leading-tight italic">
                Trauern heißt, liebevolles Erinnern."
              </h2>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ERSTE SCHRITTE */}
      <section className="py-20 bg-[#3E4C59]" data-testid="erste-schritte">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div {...fadeUp(0)}>
              <p className="uppercase tracking-[0.3em] text-xs text-[#C9A66B] font-manrope mb-3">Trauerfall</p>
              <h2 className="font-playfair text-3xl sm:text-4xl text-white mb-6">Erste Schritte</h2>
              <p className="text-white/70 font-manrope text-sm leading-relaxed mb-6">
                Im Trauerfall sind Sie nicht allein. Wir begleiten Sie vom ersten Anruf an durch alle notwendigen Schritte – ruhig, zuverlässig und einfühlsam.
              </p>
              <a
                href="tel:+491714312224"
                data-testid="erste-schritte-phone"
                className="inline-flex items-center gap-3 bg-white text-[#3E4C59] px-6 py-4 font-manrope font-bold hover:bg-[#F8F5F0] transition-colors"
              >
                <Phone size={18} />
                <div>
                  <div className="text-xs text-[#3E4C59]/60 uppercase tracking-widest font-normal mb-0.5">24/7 Notfall</div>
                  <div className="text-lg">0171 4312224</div>
                </div>
              </a>
            </motion.div>
            <motion.div {...fadeUp(0.15)}>
              <div className="space-y-4">
                {[
                  { step: "01", text: "Rufen Sie uns an – rund um die Uhr, 365 Tage." },
                  { step: "02", text: "Wir kommen sofort zu Ihnen und übernehmen die Überführung." },
                  { step: "03", text: "Wir klären alle Formalitäten: Friedhof, Pfarrei, Behörden." },
                  { step: "04", text: "Gemeinsam gestalten wir einen würdevollen Abschied nach Ihren Wünschen." },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="flex items-start gap-4 bg-white/10 p-4"
                    data-testid={`step-${i}`}
                  >
                    <span className="font-playfair text-2xl text-[#C9A66B] leading-none shrink-0">{item.step}</span>
                    <p className="text-white/80 text-sm font-manrope leading-relaxed pt-1">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-[#F8F5F0]" data-testid="bestattungen-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p {...fadeUp(0)} className="text-center uppercase tracking-[0.3em] text-xs text-[#3E4C59] font-manrope mb-3">Leistungen</motion.p>
          <motion.h2 {...fadeUp(0.1)} className="text-center font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-14">
            Unser Leistungsangebot
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="bg-white border border-[#3E4C59]/10 p-7 hover:border-[#3E4C59]/25 transition-all duration-300"
                data-testid={`bestattung-service-${i}`}
              >
                <div className="w-10 h-10 bg-[#3E4C59]/10 flex items-center justify-center mb-4">
                  <s.icon size={20} className="text-[#3E4C59]" />
                </div>
                <h3 className="font-playfair text-lg text-[#2B2B2B] mb-2">{s.label}</h3>
                <p className="text-[#2B2B2B]/55 text-sm font-manrope leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F8F5F0]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.p {...fadeUp(0)} className="text-center uppercase tracking-[0.3em] text-xs text-[#3E4C59] font-manrope mb-3">
            Häufige Fragen
          </motion.p>
          <motion.h2 {...fadeUp(0.1)} className="text-center font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-10">
            Wir antworten Ihnen
          </motion.h2>
          <motion.div {...fadeUp(0.2)} className="bg-white border border-[#3E4C59]/10 px-6 sm:px-8">
            {FAQ.map((item, i) => (
              <FAQItem key={i} index={i} q={item.q} a={item.a} open={openFAQ} setOpen={setOpenFAQ} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* VORSORGE */}
      <section className="py-20 bg-[#3E4C59]" data-testid="vorsorge-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div {...fadeUp(0)}>
              <p className="uppercase tracking-[0.3em] text-xs text-[#C9A66B] font-manrope mb-3">Vorsorge</p>
              <h2 className="font-playfair text-3xl sm:text-4xl text-white mb-5">Bestattungsvorsorge</h2>
              <p className="text-white/70 font-manrope text-sm leading-relaxed mb-4">
                Mit einer rechtzeitigen Vorsorge entlasten Sie Ihre Angehörigen und stellen sicher, dass Ihr Abschied so gestaltet wird, wie Sie es sich wünschen. Wir beraten Sie individuell und unverbindlich.
              </p>
              <p className="text-white/70 font-manrope text-sm leading-relaxed">
                Sprechen Sie uns an – in einem ruhigen Gespräch erläutern wir Ihnen alle Möglichkeiten.
              </p>
            </motion.div>
            <motion.div {...fadeUp(0.15)} className="space-y-3">
              {[
                "Persönliche Wünsche schriftlich festhalten",
                "Finanzielle Absicherung durch Vorsorgeverträge",
                "Entlastung der Angehörigen im Ernstfall",
                "Unverbindliches Beratungsgespräch jederzeit möglich",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-white/80 text-sm font-manrope">
                  <CheckCircle size={16} className="text-[#C9A66B] shrink-0" />
                  {item}
                </div>
              ))}
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  data-testid="vorsorge-cta"
                  className="inline-flex items-center gap-2 bg-[#C9A66B] text-white px-6 py-3 text-sm font-manrope font-semibold hover:bg-[#b8954f] transition-colors"
                >
                  Beratung anfragen <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 bg-[#F8F5F0]" data-testid="bestattungen-contact">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2 {...fadeUp(0)} className="font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-4">
            Wir sind für Sie da.
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className="text-[#2B2B2B]/60 font-manrope mb-8">
            Bestattungshaus · Mühlenstraße 6 · 45721 Haltern am See
          </motion.p>
          <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:023642384" className="inline-flex items-center justify-center gap-2 bg-[#3E4C59] text-white px-6 py-3 text-sm font-manrope font-semibold hover:bg-[#2e3a45] transition-colors">
              <Phone size={14} /> 02364 / 2384
            </a>
            <a href="tel:+491714312224" className="inline-flex items-center justify-center gap-2 border-2 border-[#3E4C59] text-[#3E4C59] px-6 py-3 text-sm font-manrope font-semibold hover:bg-[#3E4C59] hover:text-white transition-all">
              <Phone size={14} /> 0171 4312224 (24/7)
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Bestattungen;
